import React from 'react';

import { Title, Text, CenteredContainer } from '@nerdwallet/currency';
import { Impression } from '@nerdwallet/analytics/react/impression';
import lazy, { Hydrate } from '../../components/lazy/lazy-universal';
// Local components
import ToolLinkCards from '../../components/currency/tool-link-cards/tool-link-cards';
import { useUrlRecordContext } from '../../components/url-record';

// Configs
import { RELATED_ARTICLES } from '../../components/home-improvement-calculator/analytics-constants';
import toolLinkConfig from '../../components/home-improvement-calculator/tool-link-cards';
import questionCarouselConfig from '../../config/home-improvement-finance-calculator/question-carousel';
import resultsConfig from '../../config/home-improvement-finance-calculator/your-results';
import { EVENT_TYPE, SECTION_NAME } from '../../lib/analytics-constants';

// Lazy Load Components
const QuestionCarousel = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-question-carousel' */ '../../components/question-carousel/question-carousel'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

const DecisionTree = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-decision-tree' */ '../../components/decision-tree/decision-tree'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

const LenderCTA = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-lender-cta' */ '../../components/lender-cta/lender-cta'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

const RelatedArticles = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-related-articles' */ '../../components/currency/related-articles/related-articles'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

const Methodology = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-methodology' */ '../../components/currency/methodology/methodology'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

// Constants
const FORM_NAME = 'home-improvement';

const questionConfig = {
  questionConfig: questionCarouselConfig,
  resultsConfig,
  formName: FORM_NAME,
};

const lenderConfig = {
  formName: FORM_NAME,
};

const calculatorConfig = ({ title, synopsis }) => ({
  heading: {
    title: <Title component="h1">{title}</Title>,
    content: <Text>{synopsis}</Text>,
    mobile: <Text>{synopsis}</Text>,
  },
  body: (
    <Impression
      eventType={EVENT_TYPE.FORM_IMPRESSION}
      entityName={SECTION_NAME.MRT_HOME_IMPROVEMENT_LOAN}
      payload={{
        section_name: SECTION_NAME.MRT_HOME_IMPROVEMENT_LOAN,
        section_position: 0,
        form_name: SECTION_NAME.MRT_HOME_IMPROVEMENT_LOAN,
      }}
    >
      <DecisionTree {...questionConfig} />
    </Impression>
  ),
  resultsCTA: <LenderCTA {...lenderConfig} />,
  className: 'hif-calculator',
  additionalContent: [
    <CenteredContainer>
      <div id="read-the-basics">
        <Methodology trackingFormName="hifc-methodology" impressionTracking />
      </div>
    </CenteredContainer>,
    <RelatedArticles
      entityName={RELATED_ARTICLES.entityName}
      sectionName={RELATED_ARTICLES.sectionName}
      sectionPosition={RELATED_ARTICLES.sectionPosition}
      articles={RELATED_ARTICLES.articles}
    />,

    <ToolLinkCards
      header="Other NerdWallet Resources"
      impressionTrackingName="mortgages_home_improvement_tool_card"
      cards={toolLinkConfig.cards}
      analyticsConfig={{
        sectionName: 'mortgages_home_improvement_tool_card',
        sectionPosition: 5,
      }}
    />,
  ],
});

const HIFCResults = (): JSX.Element => {
  const { title, synopsis } = useUrlRecordContext();

  return (
    <Impression
      eventType="page_impression"
      entityName="mortgages_home_improvement_loan"
    >
      <QuestionCarousel {...calculatorConfig({ title, synopsis })} />
    </Impression>
  );
};

export default HIFCResults;
